/**
 * Pantalla de inicio de sesión.
 *
 * Es la primera pantalla del ERP y la única que ve alguien que no está
 * dentro, así que tiene dos obligaciones poco vistosas:
 *
 * - **No decir de más.** «Correo o contraseña incorrectos» y nunca «ese
 *   correo no existe»: lo segundo permite averiguar qué cuentas hay.
 * - **No perder lo escrito.** Si el servidor pide el código de doble
 *   factor, el correo y la contraseña siguen ahí; y el aviso de bloqueo
 *   temporal se muestra tal cual lo manda el servidor, que es quien sabe
 *   cuánto queda.
 */

import { useId, useRef, useState, type FormEvent } from 'react';
import { ApiError } from '../session/api-error.js';
import { useSession } from '../session/session-context.js';
import { PENDING_COMPANY_KEY } from '../shell/company-switcher.js';

type Phase = 'CREDENTIALS' | 'MFA';

export function LoginScreen(): React.ReactNode {
  const { login } = useSession();

  const [phase, setPhase] = useState<Phase>('CREDENTIALS');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Si se viene de un cambio de empresa, el NIF ya está elegido: pedirlo
  // otra vez sería obligar a copiarlo de donde acaba de salir.
  const [companyTaxId, setCompanyTaxId] = useState(
    () => localStorage.getItem(PENDING_COMPANY_KEY) ?? '',
  );
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const errorRef = useRef<HTMLParagraphElement>(null);
  const ids = {
    email: useId(),
    password: useId(),
    company: useId(),
    totp: useId(),
  };

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError(null);

    try {
      const outcome = await login({
        email: email.trim(),
        password,
        ...(companyTaxId.trim() ? { companyTaxId: companyTaxId.trim() } : {}),
        ...(phase === 'MFA' ? { totpCode: totpCode.trim() } : {}),
      });

      if (outcome.status === 'MFA_REQUIRED') {
        setPhase('MFA');
        setError(null);
      } else {
        localStorage.removeItem(PENDING_COMPANY_KEY);
      }
      // Si autenticó, el proveedor de sesión cambia de estado y esta
      // pantalla deja de estar montada: no hay nada que hacer aquí.
    } catch (caught) {
      setError(messageFor(caught));
      // El lector de pantalla tiene que enterarse del fallo aunque el foco
      // siga en el campo de la contraseña.
      errorRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login">
      <form className="login__card" onSubmit={submit} noValidate>
        <header className="login__head">
          <img className="login__logo" src="/logo.svg" alt="fibwiFIRE" />
          <h1 className="login__title">
            {phase === 'CREDENTIALS' ? 'Iniciar sesión' : 'Verificación en dos pasos'}
          </h1>
          {phase === 'MFA' && (
            <p className="login__hint">
              Introduzca el código de seis dígitos de su aplicación de autenticación.
            </p>
          )}
        </header>

        {error !== null && (
          <p className="login__error" role="alert" tabIndex={-1} ref={errorRef}>
            {error}
          </p>
        )}

        {phase === 'CREDENTIALS' ? (
          <>
            <div className="field">
              <label htmlFor={ids.email}>Correo electrónico</label>
              <input
                id={ids.email}
                type="email"
                autoComplete="username"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor={ids.password}>Contraseña</label>
              <input
                id={ids.password}
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor={ids.company}>
                NIF de la empresa <span className="field__optional">opcional</span>
              </label>
              <input
                id={ids.company}
                type="text"
                autoComplete="organization"
                value={companyTaxId}
                onChange={(e) => setCompanyTaxId(e.target.value)}
              />
              <p className="field__help">
                Sólo si su correo está dado de alta en más de una empresa.
              </p>
            </div>
          </>
        ) : (
          <div className="field">
            <label htmlFor={ids.totp}>Código de verificación</label>
            <input
              id={ids.totp}
              className="field__code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              autoFocus
              required
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
            />
          </div>
        )}

        <button className="button button--primary" type="submit" disabled={busy}>
          {busy ? 'Comprobando…' : phase === 'CREDENTIALS' ? 'Entrar' : 'Verificar'}
        </button>

        {phase === 'MFA' && (
          <button
            className="button button--quiet"
            type="button"
            onClick={() => {
              setPhase('CREDENTIALS');
              setTotpCode('');
              setError(null);
            }}
          >
            Volver
          </button>
        )}
      </form>
    </main>
  );
}

/**
 * Qué se le enseña a quien no consigue entrar.
 *
 * Un 401 en el login es «las credenciales no valen», y se dice sin
 * distinguir si falló el correo o la contraseña: distinguirlos convierte
 * la pantalla en un comprobador de cuentas existentes. Los demás errores
 * —bloqueo temporal, servidor caído— sí se cuentan como vienen, porque
 * ocultarlos sólo consigue que la persona repita lo mismo.
 */
function messageFor(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'No se ha podido contactar con el servidor.';
  }
  if (error.status === 401) {
    return 'Correo o contraseña incorrectos.';
  }
  return error.message;
}
